/**
 * One-line human summaries of tool results for collapsed tool rows.
 * Pure string logic (no React) — shared by ToolBlock and the activity group.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const SUMMARY_MAX_CHARS = 80;

function truncateSummary(text: string): string {
  return text.length > SUMMARY_MAX_CHARS ? `${text.slice(0, SUMMARY_MAX_CHARS - 1)}…` : text;
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * One-line human summary of a tool result: item counts for list-shaped
 * payloads, success/failure flags, or the first line of a text (TOON) result.
 * Null when there is nothing useful to say.
 */
export function getResultSummary(output: unknown, errorText?: string): string | null {
  if (errorText !== undefined) return null;
  if (output === undefined || output === null) return null;

  if (typeof output === 'string') {
    const firstLine = output.split('\n', 1)[0]?.trim() ?? '';
    return firstLine.length > 0 ? truncateSummary(firstLine) : null;
  }

  if (Array.isArray(output)) {
    return pluralize(output.length, 'item');
  }

  if (isRecord(output)) {
    // Standard search envelope: { count, returned, truncated, items }.
    const items = output.items;
    if (Array.isArray(items)) {
      const total = output.count;
      if (typeof total === 'number' && total > items.length) {
        return `${items.length} of ${pluralize(total, 'item')}`;
      }
      return pluralize(items.length, 'item');
    }
    if (typeof output.count === 'number') {
      return pluralize(output.count, 'result');
    }
    if (typeof output.success === 'boolean') {
      return output.success ? 'Success' : 'Failed';
    }
    if (typeof output.message === 'string' && output.message.trim().length > 0) {
      return truncateSummary(output.message.trim());
    }
  }

  return null;
}
